/**
 * D2 Precisión — Scoring de resolución espacial del resultado
 *
 * Evalúa la calidad posicional del resultado geocodificado:
 *   - Resolución del match (exact > street > locality > municipality)
 *   - Penalización por fuzzy matching bajo
 *   - Bonus por coordenadas originales del documento
 *
 * @module services/scoring/d2Precision
 * @version 1.0.0
 */

import type {
  D2PrecisionInput,
  D2PrecisionResult,
  MatchResolution,
} from '../../types/scoring';

// ============================================================================
// CONFIGURACIÓN
// ============================================================================

/**
 * Score base por resolución del match.
 * Refleja la precisión espacial inherente de cada nivel.
 *
 * ⚠️ PROCEDENCIA · auditada en `BAS1` (01-ago-2026): **estos valores no derivan
 * de nada escrito.** Los siete originales entran en `ef83a032` (15-feb-2026),
 * uno de los ocho módulos de scoring v2 redactados esa misma tarde; ni el
 * mensaje del commit ni ningún documento del repo justifica un solo número.
 * `exact_surveyed`=98 se añade después (`c4d957d5`) desde una fila de deuda
 * técnica de prioridad «Baja» (`SC-CAT-02`). Las tolerancias comentadas abajo
 * son plausibles, pero **ninguna regla escrita las convierte en estas cifras**.
 * No es una invitación a retocarlas a ojo: es la advertencia de que quien las
 * mueva tendrá que derivarlas, porque no hay nada previo que le respalde.
 *
 * ⚠️ HUECO DECLARADO · entre `locality` (40, ±200 m) y `municipality` (15,
 * ±2 km) no hay peldaño. Una parcela catastral correctamente identificada está
 * mejor atada que una localidad y peor que un portal, y hoy no cabe en ninguno:
 * `cadastral_rural`, `cadastral_centroid` y `polygon_parcel` caen a `unknown`
 * (30), que da la casualidad de caer en ese hueco — honesto por accidente, no
 * por diseño. **No se añade peldaño sin derivarlo**: el criterio de cierre es
 * el radio equivalente de la distribución real de superficies de parcela que
 * Catastro ya expone, no un número elegido. Ver
 * `OBS-SCORING-PELDANO-PARCEL-SIN-DERIVAR`.
 *
 * Peso de un cambio aquí: D2 pondera 0,40 en `computeFinalScore`, luego **un
 * punto de base mueve 0,40 de `total`**, y `total` gobierna los umbrales de
 * `computeFlags` (40 · 60 · 75). Medición de cruces en `AUDIT_BAS1` §3.
 */
const BASE_SCORE_BY_RESOLUTION: Record<MatchResolution, number> = {
  exact_surveyed: 98,  // Catastro DGC: levantamiento in situ (±5 m) — SC-CAT-02
  exact:          95,  // Portal / coordenada precisa (±5 m)
  interpolated:   80,  // Interpolación entre portales (±15 m)
  street:         60,  // Nivel calle sin portal (±50 m)
  locality:       40,  // Nivel localidad/entidad (±200 m)
  municipality:   15,  // Centroide municipal (±2 km)
  province:        5,  // Centroide provincial (±50 km)
  // ⚠️ `unknown` NO es el suelo de la escala: está por encima de `municipality`
  // (15) y `province` (5). El comentario original decía «conservador» y no lo
  // es — para una familia que declara centroide municipal, caer aquí la SOBRE-
  // puntúa. Se mantiene en 30 por decisión medida (`DOP-BAS1-UNKNOWN` = (a),
  // `BAS1` 01-ago-2026), no por inercia: bajarlo a 5 apagaría `REVISION_MANUAL`
  // (banda 40–59) en el 27,8 % de la rejilla alcanzable, es decir, dejaría de
  // pedir revisión humana justo en los elementos peor situados — silenciar un
  // aviso legítimo es sev B, peor que el estado actual (`AUDIT_BAS1` §3.1).
  // El fondo sigue vivo: un valor que significa «no sé» no debería ordenarse
  // entre valores que significan «sé esto de bien».
  // Ver `OBS-SCORING-UNKNOWN-FUERA-DE-ESCALA`.
  unknown:        30,  // No determinado — posición deliberada, no conservadora
};

/**
 * Bonus por coordenadas procedentes del documento original.
 * Si el documento ya traía coordenadas (no geocodificadas),
 * se asume que el autor las obtuvo in situ.
 */
const DOCUMENT_COORDS_BONUS = 10;

/**
 * Umbral de fuzzy score por debajo del cual se penaliza.
 * Un fuzzyScore de 1.0 = match exacto, no penaliza.
 */
const FUZZY_THRESHOLD = 0.85;

/**
 * Penalización máxima por fuzzy matching bajo.
 * Se escala linealmente entre FUZZY_THRESHOLD y 0.
 */
const FUZZY_MAX_PENALTY = 25;

/**
 * Umbrales de matchType para inferir resolución.
 * Mapeo de strings comunes de CartoCiudad/CDAU/Nominatim, ampliado en D2FX
 * (01-ago-2026) a las familias cuya resolución espacial real es conocida.
 *
 * ⚠️ Lo que NO está aquí, está fuera A PROPÓSITO. Toda cadena no mapeada cae a
 * `unknown` (base 30) por `inferResolution`, y para las familias siguientes eso
 * es la respuesta honesta, no un hueco pendiente:
 *
 *  - `fuzzy` y `filter` (10 servicios R2: DERA G3/G10/G12_36/37/38, IAPH, CGPHA,
 *    OBPH, DERAR2Geocoder, DeraR2Geocoder) — son las cadenas más emitidas del
 *    proyecto y no declaran resolución espacial alguna. Mapearlas silenciaría
 *    avisos legítimos, que es peor que no mapearlas.
 *  - `cadastral_rural`, `cadastral_centroid`, `polygon_parcel` — centroides de
 *    parcelas que pueden tener hectáreas. La escala `MatchResolution` no tiene
 *    peldaño entre `locality` (±200 m) y `municipality` (±2 km); `unknown` (30)
 *    cae de hecho en ese hueco.
 *  - `address` (CatastroGeocoderV2), `port`, `code_exact`/`code_partial`/
 *    `name_fuzzy` (DERAViaria) y las siete restantes de TorresComGeocoder.
 *  - Las variantes dinámicas —`toponym:<localType>` (NGA), `collection` (IDEE),
 *    `result.type`/`addresstype` (Overpass/Nominatim), `legacy.matchType`— no se
 *    resuelven estáticamente: `DOP-D2FX-DINAMICAS` sellado en (a), caen a
 *    `unknown` de forma explícita.
 *
 * La causa de fondo —que el mapa sea la única fuente de resolución— NO se corrige
 * aquí: `buildD2Input` acepta `resolution` como cuarto argumento, pero ningún
 * productor la construye y el transporte (`Infrastructure.matchType`) no la lleva.
 * `DOP-L3A-ARREGLO` sellado en (c): esto es (a); (b) queda como arco sucesor.
 */
const MATCH_TYPE_RESOLUTION: Record<string, MatchResolution> = {
  // Catastro DGC — levantamiento topográfico in situ (SC-CAT-02)
  catastro:        'exact_surveyed',
  catastro_r2:     'exact_surveyed',
  catastro_api:    'exact_surveyed',
  // D2FX · parcela atada por referencia catastral — misma fuente DGC que las tres
  // anteriores. `refcat` lo emite CatastroGeocoderV2 (búsqueda por RC); `cadastral`
  // sale del punto oficial INSPIRE WFS y `cadastral_urban` de xcen/ycen de OVC.
  refcat:          'exact_surveyed',
  cadastral:       'exact_surveyed',
  cadastral_urban: 'exact_surveyed',
  // CartoCiudad (claves en lowercase — se normaliza en resolveResolution)
  portal:          'exact',
  portalpk:        'exact',
  tramo:           'interpolated',
  calle:           'street',
  municipio:       'municipality',
  provincia:       'province',
  // CDAU
  numero:          'exact',
  vial:            'street',
  // D2FX · CDAUGeocoder emite `street` sobre el mismo registro `cdau_portal`
  // cuando el portal no trae NUM_PORTAL. Semántica idéntica a `vial`.
  street:          'street',
  entidad:         'locality',
  nucleo:          'locality',
  diseminado:      'locality',
  // Nominatim
  house:           'exact',
  building:        'exact',
  road:            'street',
  village:         'locality',
  town:            'locality',
  city:            'municipality',
  county:          'province',
  // Genéricos
  exact:           'exact',
  interpolated:    'interpolated',
  street_level:    'street',
  centroid:        'municipality',
  // D2FX · centroides municipales declarados como tales por su productor. Estas dos
  // cadenas BAJAN de 30 (unknown) a 15: hoy están sobrepuntuadas, porque `unknown`
  // no es el suelo de la escala — está por encima de `municipality` y `province`.
  // `municipality_centroid`: CartoCiudadGeocoder, con confianza ya tapada a 30 en origen.
  // `centroid_fallback_L3`: TorresComGeocoder, centroide municipal DERA, confianza 35.
  // ⚠️ clave en minúsculas: resolveResolution normaliza con toLowerCase() antes
  // de buscar, así que `centroid_fallback_L3` del productor entra como `..._l3`.
  municipality_centroid: 'municipality',
  centroid_fallback_l3:  'municipality',
};

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Calcula el componente D2 Precisión del scoring v2.
 *
 * Algoritmo:
 * 1. Resolver resolución efectiva (matchType → resolution, o resolution directa)
 * 2. Score base según resolución
 * 3. + Bonus por coordenadas de documento
 * 4. − Penalización por fuzzy matching bajo
 * 5. Clamp [0, 100]
 *
 * @example
 * ```ts
 * const d2 = computeD2Precision({
 *   matchType: 'portal',
 *   fuzzyScore: 0.98,
 *   resolution: 'exact',
 *   fromDocument: false,
 * });
 * // d2.score → 95 (base exact, sin penalización fuzzy)
 * ```
 */
export function computeD2Precision(input: D2PrecisionInput): D2PrecisionResult {
  const { matchType, fuzzyScore, fromDocument } = input;

  // 1. Resolver resolución efectiva
  const resolution = resolveResolution(matchType, input.resolution);

  // 2. Score base
  const baseScore = BASE_SCORE_BY_RESOLUTION[resolution];

  // 3. Bonus documento
  const documentBonus = fromDocument ? DOCUMENT_COORDS_BONUS : 0;

  // 4. Penalización fuzzy
  const fuzzyPenalty = computeFuzzyPenalty(fuzzyScore);

  // 5. Score final clamped
  const rawScore = baseScore + documentBonus - fuzzyPenalty;
  const score = Math.max(0, Math.min(100, rawScore));

  // Desglose legible
  const parts: string[] = [`base=${baseScore} (${resolution})`];
  if (documentBonus > 0) parts.push(`+${documentBonus} (documento)`);
  if (fuzzyPenalty > 0) parts.push(`-${fuzzyPenalty} (fuzzy=${fuzzyScore?.toFixed(2) ?? '?'})`);

  return {
    score,
    resolution,
    fuzzyPenalty,
    documentBonus,
    detail: `D2=${score}: ${parts.join(', ')}`,
  };
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Resuelve la resolución efectiva combinando matchType y resolución explícita.
 *
 * Prioridad:
 * 1. matchType (si mapeable) — más granular
 * 2. resolution explícita del input
 * 3. 'unknown' como fallback
 */
export function resolveResolution(
  matchType: string | undefined,
  explicitResolution: MatchResolution,
): MatchResolution {
  // matchType tiene prioridad si es mapeable
  if (matchType) {
    const normalized = matchType.toLowerCase().trim();
    const mapped = MATCH_TYPE_RESOLUTION[normalized];
    if (mapped) return mapped;
  }

  return explicitResolution;
}

/**
 * Calcula la penalización por fuzzy matching bajo.
 *
 * - fuzzyScore ≥ 0.85: sin penalización
 * - fuzzyScore < 0.85: penalización lineal hasta FUZZY_MAX_PENALTY
 * - fuzzyScore undefined: sin penalización (no hubo fuzzy)
 *
 * @returns Penalización 0-25 puntos
 */
export function computeFuzzyPenalty(fuzzyScore: number | undefined): number {
  if (fuzzyScore === undefined || fuzzyScore >= FUZZY_THRESHOLD) return 0;

  // Escala lineal: 0.85 → 0 penalty, 0.0 → 25 penalty
  const ratio = Math.max(0, FUZZY_THRESHOLD - fuzzyScore) / FUZZY_THRESHOLD;
  return Math.round(ratio * FUZZY_MAX_PENALTY);
}

/**
 * Infiere la resolución a partir de un matchType string.
 * Útil para puente de migración desde OrchestrationResult existente.
 */
export function inferResolution(matchType: string | undefined): MatchResolution {
  return resolveResolution(matchType, 'unknown');
}

/**
 * Crea D2PrecisionInput a partir de datos del OrchestrationResult existente.
 * Función puente para facilitar la migración scoring v1 → v2.
 */
export function buildD2Input(
  matchType?: string,
  fuzzyScore?: number,
  fromDocument?: boolean,
  resolution?: MatchResolution,
): D2PrecisionInput {
  const effectiveResolution = resolution ?? inferResolution(matchType);

  return {
    matchType,
    fuzzyScore,
    resolution: effectiveResolution,
    fromDocument: fromDocument ?? false,
  };
}
